<template>
  <div
    class="etx-container Container"
    :class="[isEditing ? 'etx-editing' : '', ...paragraphClassList]"
  >
    <ClientOnly>
      <div class="etx-container Container pt-0 pb-25 overflow-visible">
        <div class="etx-center Center">
          <FilterComponent
            :entries="filterEntries"
            @filters-changed="handleFiltersChanged($event)"
          />
          <div v-if="userMessage" class="message message-warning">
            {{ userMessage }}
          </div>
        </div>
      </div>

      <div class="etx-container Container pt-0 pb-0 overflow-visible">
        <div class="etx-center Center">
          <TeaserListComponent
            :load-more="true"
            :dataSource="teaserModuleDataSource"
            :page-size="pageSize"
            @selected="handleTeaserSelected($event)"
            @load-data="handleLoadTeaserListData()"
          />
        </div>
      </div>

      <div
        class="etx-container Container pt-25"
        v-if="teaserModuleDataSource?.data?.value?.length >= 1000"
      >
        <div class="etx-center Center">
          <div
            class="etx-button Button minimal active"
            @click="handleLoadTeaserListData()"
          >
            <div class="etx-button__text Button__text">
              <span>{{
                $texts('losys.references_load_more', 'Mehr laden')
              }}</span>
            </div>
            <div class="etx-button__icon Button__icon-">
              <i class="far fa-long-arrow-down"></i>
            </div>
          </div>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import {
  FilterComponent,
  LosysApiService,
  ReactiveAsyncDataSource,
  TeaserListComponent,
  type FilterEntry,
  type FilterItem,
  type LosysApiAuthResult,
  type LosysApiProjectFilter,
  type TeaserCard,
} from '@etxdev/losys-lib'
import {
  getLosysCategoryFilter,
  getCompetenciesFromLosysIds,
  APPROVAL_BY_MARKETING_IDS,
  EQUANS_COMPANIES_IDS,
  KUMA_COMPANIES_IDS,
} from '~/composables/useCompetencyTaxonomy'

const tenantId = useTenant()
const { data: competencies } = await useAsyncData(
  `getCompetencyTermsLosys-${tenantId}`,
  async () => {
    const result = await useGraphqlQuery('getCompetencyTermsLosys', {
      vid: tenantId === 'equans' ? 'competencies' : 'competencies_kuma',
    })
    const items = result.data?.entityQuery?.items ?? []
    return items
  },
)

const { $texts } = useNuxtApp()
const currentLanguage = useCurrentLanguage().value || 'de'

const { options, isEditing } = defineBlokkli({
  bundle: 'references_listing',
  globalOptions: ['spacingTop', 'spacingBottom'],
  editor: {
    addBehaviour: 'no-form',
    disableEdit: true,
    maxInstances: 1,
  },
})

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  return classList
})

const userMessage = ref('')

/**
 * init losys api service
 * use etrex.dev proxy for now, which injects client secret into request
 * Setting the authStore param is not needed. Default behavior: token is stored within service instance.
 * If you want to store data elsewhere (as in example) pass a authStore impl.
 */
const apiService: LosysApiService = new LosysApiService({
  authEndpoint:
    'https://etrex.dev/equans/proxy.php?endpoint=auth&target=https://api.referenz-verwaltung.ch/oauth/token',
  apiHost: 'https://api.referenz-verwaltung.ch/api',
  authStore: {
    set: (data: LosysApiAuthResult) => {
      localStorage.setItem(
        'losys-auth',
        JSON.stringify({
          access_token: data.access_token,
          company: undefined!, // dont need it
        }),
      )
    },
    get: (): LosysApiAuthResult | undefined => {
      const storedData = localStorage.getItem('losys-auth')
      if (!storedData) return undefined

      return JSON.parse(storedData) as LosysApiAuthResult
    },
  },
})

if (import.meta.client) {
  apiService.authenticateAndGetBasicInformation().then(() => {
    loadData()
  })
}

const pageSize: number = 100 // default page size for data sources

/**
 * filter entries for filter component
 * Is used to filter data source at a later point
 */
const filterEntries: FilterEntry<keyof LosysApiProjectFilter>[] = [
  /*  {
    id: 'companyIds',
    text: $texts('organisations.label', 'Unternehmen'),
    items: EQUANS_COMPANIES_IDS.map((c) => {
      return {
        value: `${c.id}`,
        text: c.text,
      }
    }),
    selected: [],
    multiselect: true,
  }, */
  {
    id: 'categoryIds',
    text: $texts('competencies.label', 'Kompetenzen'),
    items: getLosysCategoryFilter(competencies.value) as FilterItem[],
    selected: [],
    multiselect: true,
  },
]

/**
 * Teaser data source
 * Data source for the teaser list & slider component
 */
const contextualCompaniesIds =
  tenantId === 'kuma'
    ? KUMA_COMPANIES_IDS.map(String)
    : EQUANS_COMPANIES_IDS.map(String)

const teaserModuleDataSource: ReactiveAsyncDataSource<
  TeaserCard,
  LosysApiProjectFilter
> = new ReactiveAsyncDataSource<TeaserCard, LosysApiProjectFilter>(
  (filter: LosysApiProjectFilter): Promise<TeaserCard[]> => {
    return new Promise<TeaserCard[]>((resolve, _reject) => {
      // fetch data from api
      apiService
        .getCustomerProjects({
          ...filter,
          languages: [currentLanguage],
          expand: [
            'project_images',
            'project_categories',
            'project_properties',
          ],
          companyIds: contextualCompaniesIds,
        })
        .then((restData) => {
          // Filter by highlighted and approved projects
          const approvedData = restData
            .filter((y) => {
              const props = Object.values(y.project_properties)
              const isHighlight = props.find((y) =>
                IS_HIGHLIGHT_IDS.includes(y.projectAttributeId),
              )
              return isHighlight?.value !== 'True'
            })
            .filter((x) => {
              const props = Object.values(x.project_properties)
              const approvalByMarketing = props.find((x) =>
                APPROVAL_BY_MARKETING_IDS.includes(x.projectAttributeId),
              )
              return approvalByMarketing?.value === 'True'
            })
          // .sort(() => Math.random() - 0.5)

          // map the data into teaser card data
          const data = approvedData.map((x) => {
            let imageUrl = (
              x.project_images && x.project_images?.length > 0
                ? x.project_images[0]?.large_image
                : ''
            ).replace(
              'https://api.referenz-verwaltung.ch/static/images/project/',
              'https://ik.imagekit.io/equans/losys/',
            )
            imageUrl = `${imageUrl}?tr=w-1000,ar-3-2,fo-auto`

            const projectCompetencies = getCompetenciesFromLosysIds(
              x.project_categories.map((x) => x.categoryId),
              true,
              competencies.value,
            ).map((x) => x.name)

            return {
              properties: {
                id: `${x.id}`,
                image: {
                  url: imageUrl,
                },
                tags: projectCompetencies,
                title: {
                  small: '', // x.zipcode, // will be "time to read"
                  main: x.title,
                  sub: x.city,
                },
              },
            }
          })

          resolve(data)
        })
        .catch(() => {
          userMessage.value = $texts(
            'references.api_down',
            'Beim Abrufen der Referenzen sind Probleme aufgetreten. Bitte versuchen Sie es später erneut.',
          )
          resolve([])
        })
    })
  },
)

teaserModuleDataSource.loaded$.subscribe(() => {
  if (teaserModuleDataSource?.data?.value.length < 1) {
    userMessage.value = $texts(
      'references.no_results',
      'Mit diesen Filtern sind keine Ergebnisse verfügbar. Bitte ändern Sie Ihre Auswahl.',
    )
  }
})

/**
 * Triggers if teaser card was clicked within highlight module
 * @param card
 */
const handleTeaserSelected = (card: TeaserCard) => {
  window.location.href = `/${currentLanguage}/project/${card?.properties?.id}`
}

/**
 * handle filter component filter changes
 * triggers data source load with new api filter which is composed
 * based on the selected filter items
 * @param filters
 */
const handleFiltersChanged = (
  filters: FilterEntry<keyof LosysApiProjectFilter>[],
) => {
  userMessage.value = ''

  let apiFilter: LosysApiProjectFilter = {
    pageIndex: 0,
    pageSize: pageSize,
    languages: [currentLanguage],
  }

  // build up api filter
  filters.forEach((x) => {
    const filterKey = x.id

    apiFilter = {
      ...apiFilter,
      [filterKey]: x.selected
        .map((x) => x.value.split(',').filter((value) => value !== ''))
        .flat()
        .slice(0, 25), // max 25 items due to api limitation
    }
  })

  // trigger data source to load data for given filter, overwrite stored data instead of append
  teaserModuleDataSource.load(apiFilter, 'overwrite')
}

/**
 * Triggers load on teaser data source
 * @param filter
 */
const handleLoadTeaserListData = () => {
  // trigger data source to load data for given filter, append result to stored data
  teaserModuleDataSource.load(
    {
      pageSize: pageSize,
      pageIndex:
        teaserModuleDataSource.activeFilter.pageIndex === 0
          ? teaserModuleDataSource.activeFilter.pageIndex
          : teaserModuleDataSource.activeFilter.pageIndex * pageSize,
      expand: ['project_images', 'project_categories'],
    },
    'append',
  )
}

const loadData = () => {
  // initial load for teaser module data source
  teaserModuleDataSource.load(
    {
      pageIndex: 0,
      pageSize: pageSize,
      languages: [currentLanguage],
      expand: ['project_images'],
    },
    'append',
  )
}
</script>

<style lang="postcss">
.TeaserCard.has-hover {
  cursor: pointer;
}

ul.CustomSelect__options li {
  &:is(.level-1, .level-2, .level-3, .level-4, .level-5) {
    padding-left: calc(1rem * var(--level));
  }
  &.level-1 {
    --level: 1;
  }
  &.level-2 {
    --level: 2;
  }
  &.level-3 {
    --level: 3;
  }
  &.level-4 {
    --level: 4;
  }
  &.level-5 {
    --level: 5;
  }
}
</style>
